import { Alert, Stack } from '@ff-it/ui';
import { Breadcrumb, ItemLoader, Page, useEntity, useModel } from 'components';
import { useHasPerm } from 'core/permissions';
import { PriceList } from 'modules/supplier/pricelist/types';
import { Dispatch, ReactElement, useState } from 'react';

import invariant from 'tiny-invariant';

import { RadioPriceListRates } from '../types';
import { EditBands, EditBandsHeader, ViewBands, ViewBandsHeader } from './Bands';
import { EditRateCards, ViewRateCards } from './RateCards';
import { validateBandForm } from '../common/validateBands';
import type { SetStateAction } from 'react';
import { useSubmitHandler } from '@ff-it/form';
import { toast } from 'react-toastify';
import { KeyedMutator } from 'swr';
import { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import { hasBands } from '../common/util';
import { useRadioOutlets } from '../common/useRadioOutlets';
import { PricelistEditCard } from '../common/PricelistCard';

type EditState = 'bands' | 'ratecards' | null;

type BaseCardProps = {
  disabled: boolean;
  url: string;
  editState: EditState;
  setEditState: Dispatch<SetStateAction<EditState>>;
  mutate: KeyedMutator<any>;
  pricelist: RadioPriceListRates;
};

function BandEditCard({ disabled, pricelist, url, editState, setEditState, mutate }: BaseCardProps): ReactElement {
  const onSubmit = useSubmitHandler(
    { url: `${url}bands/`, method: 'POST' },
    {
      onSuccess: (value) => {
        mutate(value, { revalidate: false });
        toast.success('Item has been updated.');
        setEditState(null);
      },
    },
  );
  return (
    <PricelistEditCard
      view={
        <>
          <ViewBands bands={pricelist.bands} />
          {pricelist.ratecards.length > 0 && (
            <Alert variant="info" className="mt-2">
              Time bands are locked after a rate card has been created.
            </Alert>
          )}
        </>
      }
      viewHeader={<ViewBandsHeader bands={pricelist.bands} />}
      edit={<EditBands />}
      editHeader={<EditBandsHeader />}
      onSubmit={onSubmit}
      initialValues={pricelist}
      validate={validateBandForm}
      name="bands"
      state={editState}
      setState={setEditState}
      disabled={disabled || pricelist.ratecards.length > 0}
    />
  );
}

export function RateEditCard({
  disabled,
  pricelist,
  url,
  editState,
  setEditState,
  mutate,
  outlets,
}: BaseCardProps & {
  outlets: Outlet[];
}): ReactElement {
  const onSubmit = useSubmitHandler(
    { url: `${url}ratecards/`, method: 'POST' },
    {
      onSuccess: (value) => {
        mutate(value, { revalidate: false });
        toast.success('Item has been updated.');
        setEditState(null);
      },
    },
  );
  return (
    <PricelistEditCard
      header="Rate cards"
      view={<ViewRateCards bands={pricelist.bands} outlets={outlets} ratecards={pricelist.ratecards} />}
      edit={<EditRateCards bands={pricelist.bands} outlets={outlets} />}
      name="ratecards"
      state={editState}
      setState={setEditState}
      disabled={disabled}
      onSubmit={onSubmit}
      initialValues={pricelist}
    />
  );
}

export function RadioPricelistRates({ vendorId }: { vendorId: number }): ReactElement {
  const { permissions } = useModel();
  const {
    endpoint,
    item: { type },
  } = useEntity<PriceList>();
  invariant(type === 'RADIO');

  const outlets = useRadioOutlets(vendorId);
  const disabled = !useHasPerm(permissions?.change);

  const [editState, setEditState] = useState<EditState>(null);

  const url = `${endpoint}radio/`;

  return (
    <Page>
      <Breadcrumb to={null}>Radio rates</Breadcrumb>
      {!outlets.length ? (
        <Alert variant="warning">No radio outlets</Alert>
      ) : (
        <ItemLoader<RadioPriceListRates> url={url}>
          {({ item, mutate }) => (
            <Stack space="3" style={{ maxWidth: '64rem' }} mb="5">
              <BandEditCard
                editState={editState}
                setEditState={setEditState}
                disabled={disabled}
                pricelist={item}
                url={url}
                mutate={mutate}
              />
              {hasBands(item.bands) ? (
                <RateEditCard
                  editState={editState}
                  setEditState={setEditState}
                  disabled={disabled}
                  pricelist={item}
                  url={url}
                  mutate={mutate}
                  outlets={outlets}
                />
              ) : (
                <Alert variant="warning">Configure bands to set up rates</Alert>
              )}
            </Stack>
          )}
        </ItemLoader>
      )}
    </Page>
  );
}
